import { useEffect, useMemo, useState } from 'react';
import { catalogApi, REGISTRY_TENANT_ID, type CatalogCourse, type AcademicFieldRef } from '../lib/api';

/**
 * Niveaux, dans l'ordre du cursus. PILOT-L1 vient du jeu de démonstration et
 * n'a pas sa place entre les grades du secondaire et les cycles supérieurs.
 */
const LEVELS: { code: string; label: string }[] = [
  { code: 'GRADE_9', label: 'Secondaire I — Grade 9' },
  { code: 'GRADE_10', label: 'Secondaire II — Grade 10' },
  { code: 'GRADE_11', label: 'Secondaire III — Grade 11' },
  { code: 'GRADE_12', label: 'Secondaire IV — Grade 12' },
  { code: 'PROFESSIONAL', label: 'Études professionnelles' },
  { code: 'BACHELOR', label: 'Licence / Bachelor' },
  { code: 'MASTER', label: 'Master / Maîtrise' },
  { code: 'DOCTORATE', label: 'Doctorat' },
];

const PAGE_SIZE = 24;

export function CataloguePage() {
  const [fields, setFields] = useState<AcademicFieldRef[]>([]);
  const [courses, setCourses] = useState<CatalogCourse[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [level, setLevel] = useState('');
  const [fieldId, setFieldId] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Les domaines ne changent pas : un seul chargement.
  useEffect(() => {
    catalogApi
      .fields(REGISTRY_TENANT_ID)
      .then(setFields)
      .catch(() => setFields([]));
  }, []);

  // Sans ce délai, chaque frappe déclencherait une requête.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Tout changement de filtre ramène à la première page : rester en page 4
  // après avoir restreint la recherche afficherait un écran vide.
  useEffect(() => {
    setOffset(0);
  }, [debounced, level, fieldId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    catalogApi
      .courses({
        tenantId: REGISTRY_TENANT_ID,
        q: debounced || undefined,
        level: level || undefined,
        fieldId: fieldId || undefined,
        limit: PAGE_SIZE,
        offset,
      })
      .then((page) => {
        if (cancelled) return;
        setCourses(page.items);
        setTotal(page.total);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e.message || e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, level, fieldId, offset]);

  const fieldTitles = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.id, f.title])),
    [fields],
  );

  const hasFilter = Boolean(debounced || level || fieldId);
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function reset() {
    setSearch('');
    setLevel('');
    setFieldId('');
  }

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <span className="eyebrow">CATALOGUE ACADÉMIQUE</span>
          <h1>Choisir son apprentissage</h1>
          <p>
            Cherchez une matière, ou filtrez par niveau et par domaine d’étude.
          </p>
        </div>
      </div>

      <div className="filter-bar">
        <input
          className="filter-search"
          type="search"
          value={search}
          onChange={(e: any) => setSearch(e.target.value)}
          placeholder="Rechercher une matière ou un code…"
          aria-label="Rechercher une matière"
        />

        <select value={level} onChange={(e: any) => setLevel(e.target.value)} aria-label="Niveau">
          <option value="">Tous les niveaux</option>
          {LEVELS.map((l) => (
            <option key={l.code} value={l.code}>
              {l.label}
            </option>
          ))}
        </select>

        <select
          value={fieldId}
          onChange={(e: any) => setFieldId(e.target.value)}
          aria-label="Domaine d’étude"
        >
          <option value="">Tous les domaines</option>
          {fields.map((f) => (
            <option key={f.id} value={f.id}>
              {f.title}
            </option>
          ))}
        </select>

        {hasFilter && (
          <button type="button" className="button ghost filter-reset" onClick={reset}>
            Effacer
          </button>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}

      <p className="filter-count">
        {loading
          ? 'Chargement…'
          : total === 0
            ? 'Aucune matière ne correspond.'
            : `${total} matière${total > 1 ? 's' : ''}${hasFilter ? ' trouvée' + (total > 1 ? 's' : '') : ''}`}
      </p>

      {!loading && courses.length > 0 && (
        <>
          <div className="card-grid">
            {courses.map((c) => (
              <article className="card" key={c.id}>
                <span>{c.code}</span>
                <h3>{c.title}</h3>
                {c.description && <p>{c.description}</p>}
                <div className="card-tags">
                  {c.level_code && <span className="tag">{c.level_code.replace('_', ' ')}</span>}
                  {c.domain_field_id && fieldTitles[c.domain_field_id] && (
                    <span className="tag tag-domain">{fieldTitles[c.domain_field_id]}</span>
                  )}
                </div>
              </article>
            ))}
          </div>

          {pages > 1 && (
            <div className="pager">
              <button
                type="button"
                className="button secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ← Précédent
              </button>
              <span>
                Page {page} sur {pages}
              </span>
              <button
                type="button"
                className="button secondary"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
